import React from 'react';
import Icon from '../../../components/AppIcon';


const YardsDataGrid = ({ yards, selectedYards, onSelectYard, onSelectAll, onEditYard, onDeleteYard, onSort, sortConfig }) => {

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-success bg-success/10';
      case 'inactive':
        return 'text-muted-foreground bg-muted';
      case 'maintenance':
        return 'text-warning bg-warning/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getUtilizationColor = (utilization) => {
    if (utilization == null) return 'text-muted-foreground';
    if (utilization >= 80) return 'text-error';
    if (utilization >= 50) return 'text-warning';
    return 'text-success';
  };

  const getSortIcon = (column) => {
    if (sortConfig?.column !== column) return 'ChevronsUpDown';
    return sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown';
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Active';
      case 'inactive': return 'Inactive';
      case 'maintenance': return 'Maintenance';
      default: return status ? (status?.charAt(0)?.toUpperCase() + status?.slice(1)) : '—';
    }
  };

  return (
    <div className="flex-1 flex flex-col bg-background overflow-hidden">
      <div className="overflow-x-auto flex-1">
        <table className="w-full border-collapse">
          <thead className="bg-muted/50 sticky top-0 z-10">
            <tr>
              <th className="w-12 px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedYards?.length === yards?.length && yards?.length > 0}
                  onChange={(e) => onSelectAll(e?.target?.checked)}
                  className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/20"
                />
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('name')}
                  className="flex items-center space-x-1 text-xs font-semibold text-foreground hover:text-primary transition-hover"
                >
                  <span>Yard Name</span>
                  <Icon name={getSortIcon('name')} size={14} strokeWidth={2} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-xs font-semibold text-foreground">Address</span>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('region')}
                  className="flex items-center space-x-1 text-xs font-semibold text-foreground hover:text-primary transition-hover"
                >
                  <span>Region</span>
                  <Icon name={getSortIcon('region')} size={14} strokeWidth={2} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('capacity')}
                  className="flex items-center space-x-1 text-xs font-semibold text-foreground hover:text-primary transition-hover"
                >
                  <span>Capacity</span>
                  <Icon name={getSortIcon('capacity')} size={14} strokeWidth={2} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('utilization')}
                  className="flex items-center space-x-1 text-xs font-semibold text-foreground hover:text-primary transition-hover"
                >
                  <span>Utilization</span>
                  <Icon name={getSortIcon('utilization')} size={14} strokeWidth={2} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-xs font-semibold text-foreground">Manager</span>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('status')}
                  className="flex items-center space-x-1 text-xs font-semibold text-foreground hover:text-primary transition-hover"
                >
                  <span>Status</span>
                  <Icon name={getSortIcon('status')} size={14} strokeWidth={2} />
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <span className="text-xs font-semibold text-foreground">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {yards?.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center text-sm text-muted-foreground">
                  No yards found. Click "Add Yard" to create one.
                </td>
              </tr>
            ) : (
              yards?.map((yard) => (
                <tr
                  key={yard?.id}
                  className="border-b border-border hover:bg-muted/30 transition-hover"
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedYards?.includes(yard?.id)}
                      onChange={(e) => onSelectYard(yard?.id, e?.target?.checked)}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/20"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-foreground">{yard?.name || '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-muted-foreground">{yard?.address || '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-foreground">{yard?.region || '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-foreground">
                      {yard?.capacity != null ? yard?.capacity : '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {yard?.utilization != null ? (
                      <div className="flex items-center space-x-2">
                        <span className={`text-sm font-medium ${getUtilizationColor(yard?.utilization)}`}>
                          {yard?.utilization}%
                        </span>
                        <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full ${yard?.utilization >= 80 ? 'bg-error' : yard?.utilization >= 50 ? 'bg-warning' : 'bg-success'}`}
                            style={{ width: `${Math.min(yard?.utilization, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-foreground">{yard?.manager || '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md ${getStatusColor(yard?.status)}`}>
                      {getStatusLabel(yard?.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => onEditYard(yard)}
                        className="flex items-center justify-center w-8 h-8 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-md transition-hover"
                        title="Edit yard"
                      >
                        <Icon name="Edit2" size={16} strokeWidth={2} />
                      </button>
                      <button
                        onClick={() => onDeleteYard(yard?.id)}
                        className="flex items-center justify-center w-8 h-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md transition-hover"
                        title="Delete yard"
                      >
                        <Icon name="Trash2" size={16} strokeWidth={2} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default YardsDataGrid;